/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { VIEW_TYPES } from 'components/common/view-type/settings'
import { WORK_ORDERS_REVIEW_TABS } from 'containers/billing/review/work-orders-review/settings'
import { DATA_GRID_DEFAULT_PINNED_COLUMNS } from 'settings/constants/dataGrid'
import { WORK_ORDERS_REVIEW_PAGINATION } from 'settings/constants/pagination'

import { SerializedDateRange } from './persistentReviewDateRangeTypes'

const { NOT_READY } = WORK_ORDERS_REVIEW_TABS
const { LIST } = VIEW_TYPES
const { ROWS_PER_PAGE: DEFAULT_PAGE_SIZE, ROWS_PER_PAGE_OPTIONS } = WORK_ORDERS_REVIEW_PAGINATION

type PinnedColumns = typeof DATA_GRID_DEFAULT_PINNED_COLUMNS

export interface PersistentWorkOrdersReviewV2State {
  selectedTab: string
  viewType: string
  selectedFilterId: string
  pinnedColumns: PinnedColumns
  dateRange: SerializedDateRange | null
  pageSize: number
}

const initialState: PersistentWorkOrdersReviewV2State = {
  selectedTab: NOT_READY,
  viewType: LIST,
  selectedFilterId: '',
  pinnedColumns: { ...DATA_GRID_DEFAULT_PINNED_COLUMNS },
  dateRange: null,
  pageSize: DEFAULT_PAGE_SIZE,
}

const workOrdersReviewV2Slice = createSlice({
  name: 'persistentWorkOrdersReviewV2',
  initialState: { ...initialState },
  reducers: {
    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedTab = action.payload
    },
    setViewType: (state, action: PayloadAction<string>) => {
      state.viewType = action.payload
    },
    setSelectedFilterId: (state, action: PayloadAction<string>) => {
      state.selectedFilterId = action.payload
    },
    setPinnedColumns: (state, action: PayloadAction<PinnedColumns>) => {
      state.pinnedColumns = action.payload
    },
    setDateRange: (state, action: PayloadAction<SerializedDateRange | null>) => {
      state.dateRange = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
    },
    resetState: () => initialState,
  },
})

export const { setSelectedTab, setViewType, setSelectedFilterId, setPinnedColumns, setDateRange, setPageSize, resetState } =
  workOrdersReviewV2Slice.actions

// The persisted slice can predate a key, so every selector falls back to the initial value.
interface StateWithWorkOrdersReview {
  persistentWorkOrdersReviewV2?: Partial<PersistentWorkOrdersReviewV2State>
}

export const selectSelectedTab = (state: StateWithWorkOrdersReview): string => state.persistentWorkOrdersReviewV2?.selectedTab ?? NOT_READY
export const selectViewType = (state: StateWithWorkOrdersReview): string => state.persistentWorkOrdersReviewV2?.viewType ?? LIST
export const selectSelectedFilterId = (state: StateWithWorkOrdersReview): string =>
  state.persistentWorkOrdersReviewV2?.selectedFilterId ?? ''
export const selectPinnedColumns = (state: StateWithWorkOrdersReview): PinnedColumns =>
  state.persistentWorkOrdersReviewV2?.pinnedColumns ?? initialState.pinnedColumns
export const selectDateRange = (state: StateWithWorkOrdersReview): SerializedDateRange | null =>
  state.persistentWorkOrdersReviewV2?.dateRange ?? null
export const selectPageSize = (state: StateWithWorkOrdersReview): number => {
  const pageSize = state.persistentWorkOrdersReviewV2?.pageSize ?? DEFAULT_PAGE_SIZE
  return ROWS_PER_PAGE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE
}

export default workOrdersReviewV2Slice.reducer
